import { DSText, DSTextAndIcon } from '@zoe/ds-web'
import { useCallback, useState } from 'react'
import { Button, ListBox, ListBoxItem, Popover, Select, type SelectProps } from 'react-aria-components'
import styled from 'styled-components'

import { type WebsiteLocale, isWebsiteLocale, LOCALE_TO_COUNTRY_DISPLAY_MAP } from '@/types/locales'

import { useLocale } from '@/context/localeContext'

import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'
import { trackCtaClick } from '@/lib/gtm/gtmV2'
import { getLocalesForRoutePattern } from '@/lib/utils/localisationUtils'

const StyledSelect = styled(Select)`
  position: relative;
  display: inline-block;
`

const StyledButton = styled(Button)`
  background: none;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: var(--grid-8);
  color: var(--color-text-primary-inverse);

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &[data-pressed] {
    background-color: rgba(255, 255, 255, 0.1);
  }
`

const StyledPopover = styled(Popover)`
  background-color: var(--color-surface-aubergine-bold);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  margin-top: 4px;
  z-index: 2;
`

const StyledListBox = styled(ListBox)`
  outline: none;
`

const StyledListBoxItem = styled(ListBoxItem)`
  background: none;
  border: none;
  width: 100%;
  padding: var(--grid-4) var(--grid-8);
  cursor: pointer;
  color: var(--color-text-primary-inverse);
  text-align: right;
  outline: none;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &[data-focused] {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &[data-selected] {
    background-color: rgba(255, 255, 255, 0.2);
  }
`

export const FooterLocalePicker = () => {
  const { currentLocale, setLocale } = useLocale()
  const router = useTrackedRouter()
  const [isOpen, setIsOpen] = useState(false)

  const availableLocales = getLocalesForRoutePattern(router.pathname)

  const handleSelectionChange = useCallback<NonNullable<SelectProps['onSelectionChange']>>(
    (newLocale) => {
      if (typeof newLocale === 'string' && isWebsiteLocale(newLocale) && newLocale !== currentLocale) {
        setLocale(newLocale)

        // Track the locale change
        const countryName = LOCALE_TO_COUNTRY_DISPLAY_MAP[newLocale]?.name || newLocale
        trackCtaClick('footer', `Change locale to ${countryName}`, {
          link_position: 'footer',
        })
      }
    },
    [currentLocale, setLocale],
  )

  const currentCountry = LOCALE_TO_COUNTRY_DISPLAY_MAP[currentLocale]

  return (
    <StyledSelect
      selectedKey={currentLocale}
      onSelectionChange={handleSelectionChange}
      onOpenChange={setIsOpen}
      data-testid="footer-locale-picker"
    >
      <StyledButton>
        <DSTextAndIcon
          icon={{ name: isOpen ? 'chevron-up' : 'chevron-down', size: 12, color: 'inverse', position: 'right' }}
          color="primary-inverse"
          variant="fluid-label-100"
          weight="regular"
        >
          {currentCountry?.flag} {currentCountry?.name}
        </DSTextAndIcon>
      </StyledButton>
      <StyledPopover>
        <StyledListBox>
          {Object.entries(LOCALE_TO_COUNTRY_DISPLAY_MAP)
            .filter(([locale]) => locale !== currentLocale && availableLocales.includes(locale as WebsiteLocale))
            .map(([locale, country]) => (
              <StyledListBoxItem key={locale} id={locale} textValue={country.name}>
                <DSText variant="fluid-label-100" weight="regular" color="primary-inverse">
                  {country.flag} {country.name}
                </DSText>
              </StyledListBoxItem>
            ))}
        </StyledListBox>
      </StyledPopover>
    </StyledSelect>
  )
}
